import { publicEnv as env } from '@/lib/public-env'
import type { SiteContent } from '@/config/models'
import { brandAssets } from '@/config/brand-assets'
import { companyProfile } from '@/config/company'
import { professionalEmails } from '@/config/emails'
import { resolveAppEnvironment } from '@/lib/deployment'

const brandName = env.NEXT_PUBLIC_SITE_NAME ?? env.NEXT_PUBLIC_APP_NAME
const brandDescription = env.NEXT_PUBLIC_SITE_DESCRIPTION ?? env.NEXT_PUBLIC_APP_DESCRIPTION
const runtime = resolveAppEnvironment()
const configuredBrandUrl = env.NEXT_PUBLIC_SITE_URL ?? env.NEXT_PUBLIC_APP_URL
const fallbackBrandUrl = runtime === 'production' ? companyProfile.url : 'http://localhost:3000'
const configuredHostname = configuredBrandUrl ? new URL(configuredBrandUrl).hostname : undefined
const isLocalProductionUrl = runtime === 'production'
  && ['localhost', '127.0.0.1', '[::1]'].includes(configuredHostname ?? '')
const brandUrl = isLocalProductionUrl ? companyProfile.url : configuredBrandUrl ?? fallbackBrandUrl
const brandLegalName = env.NEXT_PUBLIC_SITE_LEGAL_NAME ?? companyProfile.legalName
const brandTagline = env.NEXT_PUBLIC_SITE_TAGLINE ?? companyProfile.tagline
const brandLocale = env.NEXT_PUBLIC_SITE_LOCALE ?? companyProfile.locale
const publicSupportEmail = env.NEXT_PUBLIC_SITE_SUPPORT_EMAIL ?? professionalEmails.support

const sharedServiceCards = [
  {
    label: 'Web Development',
    href: '/services',
    description: 'Fast, accessible websites and web apps built for conversion, SEO, and maintainable growth.',
  },
  {
    label: 'SaaS & BaaS Engineering',
    href: '/solutions',
    description: 'Multi-tenant SaaS, backend APIs, dashboards, subscriptions, integrations, and cloud deployment.',
  },
  {
    label: 'AI Apps & Automation',
    href: '/industries',
    description: 'AI-powered workflows, chat interfaces, review automation, data pipelines, and custom agents.',
  },
]

export const defaultSiteContent: SiteContent = {
  brand: {
    name: brandName,
    legalName: brandLegalName,
    description: brandDescription,
    tagline: brandTagline,
    url: brandUrl,
    locale: brandLocale,
    supportEmail: publicSupportEmail,
    logo: brandAssets.logo,
    logoDark: brandAssets.logoDark,
    mark: brandAssets.mark,
    favicon: brandAssets.favicon,
    appleIcon: brandAssets.appleIcon,
    logoAlt: `${brandName} logo`,
  },
  seo: {
    title: `${brandName} | Web, SaaS, and AI Solutions`,
    description: brandDescription,
    keywords: ['web development company', 'AI solutions', 'SaaS development', 'BaaS development', 'AI automation', 'mobile app development', 'Uprity'],
    canonical: brandUrl,
    ogImage: `${brandUrl}/og.png`,
    noIndex: false,
  },
  navigation: {
    utility: [
      { href: '/about', label: 'About', external: false },
      { href: '/services', label: 'Services', external: false },
      { href: '/solutions', label: 'Solutions', external: false },
      { href: '/industries', label: 'Industries', external: false },
      { href: '/case-studies', label: 'Case studies', external: false },
      { href: '/blog', label: 'Blog', external: false },
      { href: '/careers', label: 'Careers', external: false },
      { href: '/pricing', label: 'Plans', external: false },
      { href: '/contact', label: 'Contact', external: false },
    ],
    actions: [
      { href: '/get-started', label: 'Start a project', variant: 'primary', external: false },
    ],
    groups: [
      {
        label: 'Services',
        items: [
          {
            href: '/services',
            label: 'Web Development',
            description: 'Conversion-focused sites and web apps built for speed, SEO, and growth.',
            external: false,
          },
          {
            href: '/solutions',
            label: 'SaaS & AI Solutions',
            description: 'Multi-tenant SaaS, backend APIs, dashboards, and AI-powered workflows.',
            external: false,
          },
        ],
      },
      {
        href: '/industries',
        label: 'Industries',
        description: 'SMBs, agencies, SaaS founders, and service businesses.',
        external: false,
      },
      {
        href: '/case-studies',
        label: 'Case Studies',
        description: 'Proof from product work and implementation playbooks.',
        external: false,
      },
      {
        label: 'Insights',
        items: [
          {
            href: '/blog',
            label: 'Blog',
            description: 'Practical notes on web, SaaS, AI, and automation.',
            external: false,
          },
        ],
      },
      {
        label: 'Company',
        items: [
          {
            href: '/about',
            label: 'About',
            description: 'Who we are and what we optimize for.',
            external: false,
          },
          {
            href: '/pricing',
            label: 'Plans',
            description: 'Engagement models for launch, product, and automation work.',
            external: false,
          },
          {
            href: '/careers',
            label: 'Careers',
            description: 'Open roles for practical builders.',
            external: false,
          },
          {
            href: '/contact',
            label: 'Contact',
            description: 'Tell us what you want to build or automate.',
            external: false,
          },
        ],
      },
    ],
  },
  socials: [
    { label: 'X', href: env.NEXT_PUBLIC_SITE_X ?? companyProfile.socials.x, external: true },
    { label: 'GitHub', href: env.NEXT_PUBLIC_SITE_GITHUB ?? companyProfile.socials.github, external: true },
    { label: 'LinkedIn', href: env.NEXT_PUBLIC_SITE_LINKEDIN ?? companyProfile.socials.linkedin, external: true },
  ],
  services: sharedServiceCards,
  footer: {
    description: brandDescription,
    utilityLabel: 'Utility links',
    socialsLabel: 'Socials',
    servicesLabel: 'Services',
    copyright: `© ${new Date().getFullYear()} ${brandLegalName}. All rights reserved.`,
  },
  pages: {
    home: {
      seo: {
        title: `${brandName} | Web, SaaS, AI Apps, and Automation`,
        description: brandDescription,
        keywords: ['web development company', 'SaaS development', 'AI web apps', 'AI automation', 'mobile apps', 'Uprity reputation management'],
        canonical: brandUrl,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Mobile-first product engineering studio',
        title: brandName,
        description: `${brandDescription} We combine product strategy, modern web engineering, and practical AI to ship systems that are useful from day one.`,
        primaryCta: { href: '/get-started', label: 'Start a project', variant: 'primary', external: false },
        secondaryCta: { href: companyProfile.product.url, label: `Explore ${companyProfile.product.name}`, variant: 'secondary', external: true },
        image: {
          src: '/hero-marketing.svg',
          alt: `${brandName} web and AI product preview`,
          width: 960,
          height: 760,
        },
      },
      socialProof: {
        label: 'Built for founders, agencies, and SMB operators who need practical software outcomes',
        logos: [
          { label: 'SMBs', external: false },
          { label: 'Agencies', external: false },
          { label: 'SaaS founders', external: false },
          { label: 'Service businesses', external: false },
        ],
      },
      stats: [
        { value: '5+', label: 'solution tracks', description: 'Websites, SaaS, BaaS, AI apps, mobile apps, and automation workflows.' },
        { value: '1', label: 'in-house SaaS product', description: `${companyProfile.product.name} validates our reputation-management playbook in production.` },
        { value: 'AI-first', label: 'delivery approach', description: 'We use AI where it reduces repetitive work without hiding engineering quality.' },
      ],
      servicesPreview: {
        title: 'Services built around outcomes',
        description: 'Pick the engagement model that matches your current bottleneck: launch, automate, scale, or modernize.',
      },
      solutionsPreview: {
        title: 'Productized solutions',
        description: 'Nexline packages strategy, design, engineering, and AI into focused tracks with clear deliverables.',
        items: [
          {
            title: 'Launch a conversion website',
            description: 'Plan, design, build, and deploy a mobile-first marketing site with SEO and analytics included.',
            href: '/get-started',
            ctaLabel: 'Plan launch',
          },
          {
            title: 'Build a SaaS platform',
            description: 'Ship dashboards, APIs, authentication, payments, admin tooling, and cloud infrastructure.',
            href: '/solutions',
            ctaLabel: 'See SaaS track',
          },
          {
            title: 'Automate with AI',
            description: 'Turn manual operations into AI-assisted workflows, agents, review replies, and lead-routing systems.',
            href: '/services',
            ctaLabel: 'Explore AI work',
          },
        ],
      },
      caseStudyPreview: {
        label: 'In-house product',
        title: `${companyProfile.product.name}: AI reputation management for SMBs and agencies`,
        description: companyProfile.product.description,
        result: 'A focused alternative for teams that need review generation, response assistance, and reputation workflows without enterprise complexity.',
        quote: 'Uprity gives Nexline a real product laboratory for AI automation, local business workflows, and SaaS operations.',
        author: brandLegalName,
        role: 'Platform owner',
        company: brandLegalName,
        href: companyProfile.product.caseStudyPath,
        metrics: [
            { value: '24/7', label: 'AI-assisted reputation workflows' },
            { value: 'SMB', label: 'positioned for agencies and local operators' },
        ],
      },
      testimonials: [
        {
          quote: 'The right web platform should be clear for customers, maintainable for teams, and ready for the next workflow.',
          author: 'Nexline delivery team',
          role: 'Product engineering',
          company: brandLegalName,
        },
        {
          quote: 'AI is most useful when it removes repetitive work and keeps humans in control of quality.',
          author: 'Nexline AI team',
          role: 'Automation practice',
          company: brandLegalName,
        },
      ],
      faq: [
        {
          question: 'What does Nexline Global build?',
          answer: 'We build websites, SaaS products, backend systems, mobile apps, AI-powered web apps, and automation workflows.',
        },
        {
          question: 'Can Nexline help with AI automation for an existing business?',
          answer: 'Yes. We can map repetitive workflows, identify safe automation opportunities, and build AI-assisted systems around your current tools.',
        },
        {
          question: `What is ${companyProfile.product.name}?`,
          answer: companyProfile.product.description,
        },
      ],
      finalCta: {
        eyebrow: 'Ready to build',
        title: 'Turn your website, SaaS idea, or workflow bottleneck into shipped software',
        description: 'Share the business goal, current constraints, and target users. We will shape the right web, mobile, backend, or AI delivery path.',
        primaryCta: { href: '/get-started', label: 'Start a project', variant: 'primary', external: false },
        secondaryCta: { href: '/contact', label: 'Talk to Nexline', variant: 'secondary', external: false },
      },
      highlights: [
        {
          icon: 'palette',
          title: 'Mobile-first interfaces',
          description: 'Responsive layouts, readable content, fast loading, and clear CTAs come first.',
        },
        {
          icon: 'sparkles',
          title: 'AI where it helps',
          description: 'We add AI to reduce manual work, improve speed, and keep humans in control of quality.',
        },
        {
          icon: 'shield',
          title: 'Maintainable engineering',
          description: 'Typed config, tested flows, and clear architecture make future changes safer.',
        },
      ],
      preview: {
        title: 'Delivery system preview',
        description: 'Nexline projects move through focused discovery, scoped build work, validation, and launch support.',
        tabs: [
          {
            value: 'discover',
            title: 'Discover',
            description: 'Clarify users, business goals, constraints, integrations, timeline, and success metrics.',
          },
          {
            value: 'build',
            title: 'Build',
            description: 'Design and engineer the website, SaaS flow, mobile surface, backend, or automation workflow.',
          },
          {
            value: 'launch',
            title: 'Launch',
            description: 'Validate performance, forms, SEO, analytics, reliability, and handoff documentation.',
          },
        ],
        form: {
          nameLabel: 'Project name',
          typeLabel: 'Surface',
          notesLabel: 'Notes',
          notesDefault: 'Describe the business goal, current stack, and target launch window.',
          namePlaceholder: 'Website, SaaS MVP, AI workflow',
          notesPlaceholder: 'Key features, integrations, users, and timeline',
        },
        alert: {
          title: 'Quality is part of the baseline',
          description: 'Responsive UX, accessibility, SEO basics, validation, and deployment readiness are handled before launch.',
        },
        tooltip: 'Project scope stays visible so delivery does not drift.',
        dialog: {
          title: 'Project kickoff',
          description: 'Confirm scope, owners, success metrics, and launch milestones before build work starts.',
          primaryAction: 'Start',
          secondaryAction: 'Review',
        },
      },
      accordion: {
        title: 'How we work',
        items: [
          {
            title: 'How do we start?',
            content: 'We begin with goals, users, constraints, stack, budget, and launch timeline before recommending a build path.',
          },
          {
            title: 'How do we use AI?',
            content: 'We apply AI to repeatable workflows such as intake, reviews, content drafts, routing, reporting, and support assistance.',
          },
        ],
      },
      checklist: {
        title: 'Project readiness checklist',
        items: [
          'Business goal, target users, and success metrics are clear.',
          'Core pages, workflows, integrations, and data requirements are mapped.',
          'Launch, analytics, SEO, support, and iteration plans are defined.',
        ],
      },
    },
    about: {
      seo: {
        title: `About | ${brandName}`,
        description: `${brandDescription} Learn how Nexline Global works across web, SaaS, mobile, backend, and AI automation projects.`,
        keywords: ['about Nexline Global', 'web development company', 'AI solutions company', 'SaaS development'],
        canonical: `${brandUrl}/about`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'About',
        title: 'A product engineering partner for modern web and AI systems',
        description: 'Nexline Global combines business-aware planning, mobile-first interfaces, scalable backend engineering, and practical AI automation.',
        primaryCta: { href: '/contact', label: 'Talk to us', variant: 'primary', external: false },
        secondaryCta: { href: '/case-studies', label: 'See results', variant: 'secondary', external: false },
      },
      stats: [
        { value: 'Web', label: 'conversion sites and apps' },
        { value: 'SaaS', label: 'platform and backend builds' },
        { value: 'AI', label: 'automation and product intelligence' },
      ],
      sections: [
        {
          title: 'What we optimize for',
          description: 'Software that is clear for users, maintainable for teams, and useful for the business.',
          items: [
            { title: 'Clarity', description: 'Every project starts with the business goal, target user, and launch constraints.' },
            { title: 'Speed', description: 'We prefer pragmatic architectures that ship quickly without creating avoidable technical debt.' },
            { title: 'AI fit', description: 'Automation is scoped where it saves time, improves response quality, or unlocks new workflows.' },
          ],
        },
      ],
      faq: [
        {
          question: 'Does Nexline Global only build websites?',
          answer: 'No. We build websites, SaaS products, BaaS systems, AI-powered web apps, mobile apps, and automations.',
        },
      ],
      cta: {
        eyebrow: "Let's build",
        title: 'Ready to discuss your product or automation goal?',
        description: 'Send us the context and we will help shape the right delivery path.',
        primaryCta: { href: '/contact', label: 'Contact us', variant: 'primary', external: false },
        secondaryCta: { href: '/pricing', label: 'Review pricing', variant: 'secondary', external: false },
      },
    },
    servicesPage: {
      seo: {
        title: `Services | ${brandName}`,
        description: 'Web development, SaaS engineering, backend systems, AI-powered apps, mobile apps, and AI automation services.',
        keywords: ['web development services', 'SaaS development services', 'AI automation services', 'mobile app development', 'backend development'],
        canonical: `${brandUrl}/services`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Services',
        title: 'Services for websites, SaaS products, mobile apps, and AI workflows',
        description: 'Choose a focused service track or combine design, engineering, backend, and AI into one delivery plan.',
        primaryCta: { href: '/contact', label: 'Discuss services', variant: 'primary', external: false },
      },
      stats: [],
      sections: [
        {
          title: 'Core services',
          description: 'The practical work Nexline Global delivers for founders, SMBs, agencies, and operators.',
          items: sharedServiceCards.map(service => ({
            title: service.label,
            description: service.description ?? brandDescription,
            href: service.href,
            tags: ['Nexline service'],
          })),
        },
      ],
      faq: [],
      cta: {
        eyebrow: 'Need a custom mix?',
        title: 'Combine web, backend, mobile, and AI into one delivery plan',
        description: 'We can scope a build around your launch date, existing stack, budget, and internal team capacity.',
        primaryCta: { href: '/contact', label: 'Contact sales', variant: 'primary', external: false },
      },
    },
    solutionsPage: {
      seo: {
        title: `Solutions | ${brandName}`,
        description: 'Productized Nexline Global solutions for websites, SaaS platforms, AI apps, review automation, and mobile products.',
        keywords: ['SaaS solutions', 'AI web app solutions', 'review management software', 'AI automation', 'mobile app solutions'],
        canonical: `${brandUrl}/solutions`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Solutions',
        title: 'Solutions packaged for common business problems',
        description: 'Move from idea to shipped software with focused tracks for launch, scale, automation, and reputation management.',
        primaryCta: { href: '/contact', label: 'Plan a solution', variant: 'primary', external: false },
      },
      stats: [],
      sections: [
        {
          title: 'Solution tracks',
          description: 'Focused ways to engage Nexline Global based on what you need to ship.',
          items: [
            { title: 'Website launch system', description: 'Mobile-first marketing sites with SEO, analytics, forms, and performance discipline.', href: '/get-started', tags: ['web'] },
            { title: 'SaaS MVP build', description: 'Auth, billing, dashboards, data models, admin workflows, and deployment for a focused first release.', href: '/pricing', tags: ['saas'] },
            { title: `${companyProfile.product.name} reputation stack`, description: 'AI-assisted review, reputation, and AI visibility workflows inspired by our in-house SaaS product.', href: companyProfile.product.url, external: true, tags: ['AI reputation', 'AI visibility'] },
          ],
        },
      ],
      faq: [],
      cta: {
        eyebrow: 'Need a fit-for-purpose solution?',
        title: 'Shape a delivery track around your actual business bottleneck',
        description: 'We can start with a website, SaaS MVP, internal automation, or AI-powered customer workflow.',
        primaryCta: { href: '/contact', label: 'Start a conversation', variant: 'primary', external: false },
      },
    },
    industriesPage: {
      seo: {
        title: `Industries | ${brandName}`,
        description: 'Nexline Global works with SMBs, agencies, SaaS founders, service businesses, and operators adopting AI.',
        keywords: ['SMB web development', 'agency software partner', 'SaaS founders', 'AI automation for businesses'],
        canonical: `${brandUrl}/industries`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Industries',
        title: 'Built for service businesses, SaaS teams, and agencies',
        description: 'We focus on markets where better web systems, automation, and reputation workflows can directly improve growth.',
        primaryCta: { href: '/contact', label: 'Discuss your market', variant: 'primary', external: false },
      },
      stats: [],
      sections: [
        {
          title: 'Markets we serve',
          description: 'Teams that need stronger software execution without enterprise complexity.',
          items: [
            { title: 'SMBs and local operators', description: 'Websites, lead capture, review workflows, and AI-assisted customer operations.' },
            { title: 'Agencies', description: 'Reliable engineering support for client websites, SaaS builds, and automation retainers.' },
            { title: 'SaaS founders', description: 'MVP architecture, dashboards, subscriptions, analytics, and launch-ready marketing pages.' },
          ],
        },
      ],
      faq: [],
      cta: {
        eyebrow: 'Market-ready',
        title: 'Build software around the users and channels that matter most',
        description: 'We tailor the roadmap to your market, internal team, and growth stage.',
        primaryCta: { href: '/contact', label: 'Talk to us', variant: 'primary', external: false },
      },
    },
    caseStudiesPage: {
      seo: {
        title: `Case Studies | ${brandName}`,
        description: 'Nexline Global product proof, implementation stories, and software delivery examples.',
        keywords: ['case studies', 'web development results', 'SaaS case study', 'AI automation case study', 'Uprity'],
        canonical: `${brandUrl}/case-studies`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Case studies',
        title: 'Proof from product work and implementation playbooks',
        description: 'Featured stories show the kinds of outcomes Nexline Global is building toward across websites, SaaS, and AI automation.',
        primaryCta: { href: '/contact', label: 'Share your story', variant: 'primary', external: false },
      },
      stats: [],
      sections: [
        {
          title: 'Featured results',
          description: 'Representative product and delivery stories.',
          items: [
            {
              title: 'Nexline website relaunch',
              description: 'A mobile-first site content system built to centralize brand, SEO, service, and product messaging.',
              href: '/case-studies/nexline-website-relaunch',
              meta: 'brand system',
              tags: ['website', 'SEO'],
            },
            {
              title: `${companyProfile.product.name} product track`,
              description: 'An AI-powered review and reputation platform for SMBs and agencies.',
              href: companyProfile.product.caseStudyPath,
              meta: 'in-house SaaS',
              tags: ['AI', 'SaaS'],
            },
          ],
        },
      ],
      faq: [],
    },
    caseStudies: {
      bySlug: {
        'nexline-website-relaunch': {
          seo: {
            title: `Nexline website relaunch | ${brandName}`,
            description: 'How Nexline Global centralized brand, SEO, and service messaging for a stronger public site.',
            keywords: ['case study', 'website relaunch', 'brand config', 'Nexline Global'],
            canonical: `${brandUrl}/case-studies/nexline-website-relaunch`,
            ogImage: `${brandUrl}/og.png`,
            noIndex: false,
          },
          hero: {
            eyebrow: 'Case study',
            title: 'Nexline website relaunch',
            description: 'A public website rebuild focused on mobile-first UX, centralized company configuration, and stronger AI-services positioning.',
            badge: 'brand system',
          },
          stats: [],
          meta: [
            { title: 'Industry', description: 'Web and AI services' },
            { title: 'Focus', description: 'Messaging and configuration' },
          ],
          sections: [
            { title: 'Challenge', description: 'The public site had generic copy and scattered business messaging that made Nexline look less credible than its actual capabilities.' },
            { title: 'Approach', description: 'We centralized brand credentials, services, product references, SEO, and page copy in the typed config layer.' },
            { title: 'Outcome', description: 'Future company-name, credential, social, and positioning updates now flow through shared configuration instead of page-by-page edits.' },
          ],
          related: [
            { title: `${companyProfile.product.name} product track`, description: 'How our in-house SaaS shapes reputation-management positioning.', href: companyProfile.product.caseStudyPath },
          ],
          faq: [
            { question: 'What changed?', answer: 'Company credentials, service messaging, product positioning, and SEO now resolve from the shared content config.' },
          ],
          cta: {
            title: 'Want a similar outcome?',
            description: 'Tell us what needs to change and we will shape the right web, SaaS, or AI delivery plan.',
            primaryCta: { href: '/contact', label: 'Start a project', variant: 'primary', external: false },
          },
        },
        'uprity-product-track': {
          seo: {
            title: `${companyProfile.product.name} product track`,
            description: companyProfile.product.description,
            keywords: ['Uprity', 'review management', 'reputation management', 'AI visibility', 'AI SaaS', 'SMB software'],
            canonical: `${brandUrl}${companyProfile.product.caseStudyPath}`,
            ogImage: `${brandUrl}${companyProfile.product.caseStudyPath}/opengraph-image`,
            noIndex: false,
          },
          hero: {
            eyebrow: 'Case study',
            title: `${companyProfile.product.name} product track`,
            description: companyProfile.product.description,
          },
          stats: [],
          meta: [{ title: 'Scope', description: 'AI-powered review and reputation management' }],
          sections: [{ title: 'Outcome', description: 'The product gives Nexline hands-on experience with review workflows, AI response assistance, AI visibility, SMB positioning, and SaaS operations.' }],
          related: [{ title: 'Nexline website relaunch', description: 'How brand and service messaging became centralized.', href: '/case-studies/nexline-website-relaunch' }],
          faq: [],
          cta: {
            eyebrow: 'See Uprity in action',
            title: companyProfile.product.tagline,
            description: 'Explore the live Uprity platform for AI-powered reputation management and AI visibility.',
            primaryCta: { href: companyProfile.product.url, label: 'Visit Uprity', variant: 'primary', external: true },
          },
        },
      },
    },
    blogPage: {
      seo: {
        title: `Blog | ${brandName}`,
        description: 'Nexline Global articles on web development, SaaS builds, AI automation, and reputation-management products.',
        keywords: ['web development blog', 'AI automation blog', 'SaaS development', 'review management'],
        canonical: `${brandUrl}/blog`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Blog',
        title: 'Insights on web, SaaS, AI, and automation',
        description: 'Practical notes from building websites, SaaS products, AI-powered apps, and reputation workflows.',
      },
      stats: [],
      sections: [
        {
          title: 'Latest articles',
          description: 'Recent posts and implementation notes.',
          items: [
            { title: 'Why company websites need a single content source of truth', description: 'How central config reduces stale brand, SEO, and service copy.', href: '/blog/website-content-source', meta: '5 min read' },
            { title: 'Where AI automation creates real business value', description: 'A practical filter for deciding what to automate first.', href: '/blog/ai-automation-value', meta: '4 min read' },
          ],
        },
      ],
      faq: [],
    },
    blogPosts: {
      bySlug: {
        'website-content-source': {
          seo: {
            title: `Single source of truth for company websites | ${brandName}`,
            description: 'A guide to centralizing brand, SEO, service, and product content.',
            keywords: ['blog', 'website content strategy', 'brand config', 'SEO'],
            canonical: `${brandUrl}/blog/website-content-source`,
            ogImage: `${brandUrl}/og.png`,
            noIndex: false,
          },
          hero: {
            eyebrow: 'Article',
            title: 'Why company websites need a single content source of truth',
            description: 'A short walkthrough of how central configuration keeps brand and service messaging consistent.',
            badge: '5 min read',
          },
          stats: [],
          meta: [{ title: 'Author', description: brandLegalName }],
          sections: [
            { title: 'Why it matters', description: 'Company names, credentials, CTAs, SEO, and product references go stale quickly when they are scattered across files.' },
            { title: 'How it works', description: 'Pages render typed content from shared config, while components stay focused on layout and interaction.' },
          ],
          related: [{ title: 'Where AI automation creates real business value', description: 'Why some workflows are worth automating first.', href: '/blog/ai-automation-value' }],
          faq: [],
        },
        'ai-automation-value': {
          seo: {
            title: `Where AI automation creates business value | ${brandName}`,
            description: 'How to pick automation workflows that save time without reducing quality.',
            keywords: ['blog', 'AI automation', 'workflow automation', 'business automation'],
            canonical: `${brandUrl}/blog/ai-automation-value`,
            ogImage: `${brandUrl}/og.png`,
            noIndex: false,
          },
          hero: {
            eyebrow: 'Article',
            title: 'Where AI automation creates real business value',
            description: 'The strongest automation candidates are repetitive, measurable, and safe to review.',
            badge: '4 min read',
          },
          stats: [],
          meta: [],
          sections: [{ title: 'Approach', description: 'Start with lead intake, review responses, reporting, routing, content drafts, and internal operations before automating high-risk decisions.' }],
          related: [{ title: 'Why company websites need a single content source of truth', description: 'A content-driven route pattern.', href: '/blog/website-content-source' }],
          faq: [],
        },
      },
    },
    careersPage: {
      seo: {
        title: `Careers | ${brandName}`,
        description: 'Open roles and a simple application flow.',
        keywords: ['careers', 'jobs', 'web development', 'AI engineering'],
        canonical: `${brandUrl}/careers`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Careers',
        title: 'Join a team building web, SaaS, and AI products',
        description: 'We look for practical builders who care about clear interfaces, reliable systems, and useful AI.',
        primaryCta: { href: '/careers/apply', label: 'Apply now', variant: 'primary', external: false },
      },
      sections: [
        {
          title: 'Open roles',
          description: 'Roles that match the work Nexline Global is growing into.',
          items: [
            { title: 'Frontend engineer', description: 'Build responsive, accessible, conversion-focused interfaces.', href: '/careers/apply', meta: 'Remote' },
            { title: 'AI automation engineer', description: 'Build practical AI workflows, integrations, and operational tooling.', href: '/careers/apply', meta: 'Remote' },
          ],
        },
        {
          title: 'Hiring flow',
          description: 'A straightforward application process.',
          layout: 'timeline',
          items: [
            { title: 'Apply', description: 'Submit the application form.' },
            { title: 'Intro call', description: 'Talk through goals and fit.' },
            { title: 'Project chat', description: 'Discuss how you think and build.' },
          ],
        },
      ],
      stats: [],
      faq: [],
      cta: {
        eyebrow: 'Ready to apply?',
        title: 'Start your application in a few minutes',
        description: 'Share your background and we will follow up.',
        primaryCta: { href: '/careers/apply', label: 'Apply now', variant: 'primary', external: false },
      },
    },
    careersApplyPage: {
      seo: {
        title: `Apply | ${brandName}`,
        description: 'A simple application flow for careers.',
        keywords: ['careers', 'apply', 'jobs'],
        canonical: `${brandUrl}/careers/apply`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Apply',
        title: 'Application flow',
        description: 'Tell us a bit about yourself and the kind of work you want to do.',
      },
      sections: [
        {
          title: 'Application steps',
          description: 'A short path from form submission to follow-up.',
          layout: 'timeline',
          items: [
            { title: 'Fill the form', description: 'Share your contact details and background.' },
            { title: 'We review', description: 'We check fit and align on the role.' },
            { title: 'Follow-up', description: 'We reach out with the next step.' },
          ],
        },
      ],
      stats: [],
      faq: [],
      cta: {
        title: 'Ready to apply?',
        description: "Send the form and we'll get back to you.",
        primaryCta: { href: '/careers', label: 'Back to careers', variant: 'primary', external: false },
      },
    },
    contactPage: {
      seo: {
        title: `Contact | ${brandName}`,
        description: 'Contact Nexline Global for web development, SaaS engineering, mobile apps, AI-powered apps, and automation projects.',
        keywords: ['contact Nexline Global', 'web development quote', 'AI automation quote', 'SaaS development quote'],
        canonical: `${brandUrl}/contact`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Contact',
        title: 'Tell us what you want to build or automate',
        description: 'Use the form or direct channels to share your website, SaaS, mobile app, backend, or AI automation requirement.',
        primaryCta: { href: `mailto:${professionalEmails.sales}`, label: 'Email sales', variant: 'primary', external: true },
      },
      sections: [
        {
          title: 'Contact options',
          description: 'Multiple ways to get in touch.',
          items: [
            { title: 'General', description: professionalEmails.general, href: `mailto:${professionalEmails.general}` },
            { title: 'Sales', description: professionalEmails.sales, href: `mailto:${professionalEmails.sales}` },
            { title: 'Support', description: publicSupportEmail, href: `mailto:${publicSupportEmail}` },
            { title: 'Security', description: professionalEmails.security, href: `mailto:${professionalEmails.security}` },
            { title: 'GitHub', description: env.NEXT_PUBLIC_SITE_GITHUB ?? companyProfile.socials.github, href: env.NEXT_PUBLIC_SITE_GITHUB ?? companyProfile.socials.github },
            { title: 'LinkedIn', description: env.NEXT_PUBLIC_SITE_LINKEDIN ?? companyProfile.socials.linkedin, href: env.NEXT_PUBLIC_SITE_LINKEDIN ?? companyProfile.socials.linkedin },
          ],
        },
      ],
      stats: [],
      faq: [],
      cta: {
        title: 'Tell us what you need',
        description: 'We can scope a focused build, automation sprint, or product roadmap around your constraints.',
        primaryCta: { href: `mailto:${professionalEmails.sales}`, label: 'Email sales', variant: 'primary', external: true },
      },
    },
    pricingPage: {
      seo: {
        title: `Plans | ${brandName}`,
        description: 'Nexline Global engagement plans for websites, SaaS builds, AI automation, and product engineering.',
        keywords: ['web development pricing', 'SaaS development plans', 'AI automation pricing', 'product engineering packages'],
        canonical: `${brandUrl}/pricing`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Plans',
        title: 'Engagement models for launch, product, and automation work',
        description: 'Choose a starting point. Final scope depends on features, integrations, timeline, and support needs.',
        primaryCta: { href: '/contact', label: 'Request a quote', variant: 'primary', external: false },
      },
      sections: [
        {
          title: 'Packages',
          description: 'Starting points for common Nexline Global engagements.',
          items: [
            { title: 'Launch Website', description: 'Mobile-first website, core pages, SEO setup, forms, analytics, and deployment.', meta: 'Best for SMBs' },
            { title: 'SaaS MVP', description: 'Product architecture, auth, dashboard, backend, payment flow, admin tooling, and launch support.', meta: 'Best for founders' },
            { title: 'AI Automation Sprint', description: 'Workflow discovery, integration plan, AI-assisted process, monitoring, and team handoff.', meta: 'Best for operations' },
          ],
        },
      ],
      stats: [],
      faq: [
        { question: 'Are these fixed prices?', answer: 'No. They are engagement models. We quote after understanding scope, integrations, timeline, and support requirements.' },
      ],
      cta: {
        eyebrow: 'Need a tailored plan?',
        title: "Let's scope the right package for your build",
        description: 'Share your goal and constraints so we can recommend a practical delivery path.',
        primaryCta: { href: '/contact', label: 'Contact us', variant: 'primary', external: false },
      },
    },
    privacyPage: {
      seo: {
        title: `Privacy | ${brandName}`,
        description: `${brandName} privacy overview for website visitors, leads, and applicants.`,
        keywords: ['privacy', 'policy'],
        canonical: `${brandUrl}/privacy`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Privacy',
        title: 'Privacy and data use',
        description: 'A configurable legal page with clear sections and minimal styling.',
      },
      stats: [],
      sections: [
        {
          title: 'Summary',
          description: 'This site only collects the information needed to operate and support the experience.',
          items: [
            { title: 'What we collect', description: 'Only what is necessary for the configured product experience.' },
            { title: 'How we use it', description: 'To deliver the public site, respond to inquiries, and improve the experience.' },
            { title: 'Privacy contact', description: professionalEmails.privacy, href: `mailto:${professionalEmails.privacy}` },
          ],
        },
      ],
      faq: [],
    },
    termsPage: {
      seo: {
        title: `Terms | ${brandName}`,
        description: `${brandName} website terms overview.`,
        keywords: ['terms', 'legal'],
        canonical: `${brandUrl}/terms`,
        ogImage: `${brandUrl}/og.png`,
        noIndex: false,
      },
      hero: {
        eyebrow: 'Terms',
        title: 'Terms of use',
        description: 'A flexible legal page that can be customized per deployment.',
      },
      stats: [],
      sections: [
        {
          title: 'Agreement',
          description: 'Use of the site is subject to the terms configured for this deployment.',
          items: [
            { title: 'Acceptable use', description: 'Use the site responsibly and in accordance with the applicable terms.' },
            { title: 'Availability', description: 'Features and routes may change as the deployment evolves.' },
            { title: 'Billing questions', description: professionalEmails.billing, href: `mailto:${professionalEmails.billing}` },
          ],
        },
      ],
      faq: [],
    },
  },
}
